import httpRequest from '../../utilities/js/httpRequest/httpRequest';
import productListStore from '../../stores/product/ProductListStore';
import productService from '../product/ProductService';
import jsGlobalConfig from '../../globals/jsGlobalConfig';
import encodeFormData from '../../utilities/js/encodeFormData/encodeFormData';
import pigeon from '../../utilities/js/pigeon/pigeon.js';
import tracking from '../../utilities/js/tracking/Tracking';
import type AddToBasketResponse from '../../types/AddToBasketResponse';
import {getTrackingListValue} from '../tracking/getTrackingListValue';

export interface Parameters {
  [key: string]: string[]
}

type ListElementPlacementMode = 'replace'|'prepend'|'append';

interface ChangeParameterOptions {
  pageLoadAllowed?: boolean;
  elementPlacement?: ListElementPlacementMode;
}

// eslint-disable-next-line no-undef
export {ListElementPlacementMode, ChangeParameterOptions};

class ProductsListService {
  static pageParameterName: string = 'p';
  static sortParameterName: string = 'sortBy';
  static searchParameterName: string = 'q';

  protected urls: {
    addToBasket: string,
    addToWishlist: string,
    changeQuantity: string,
    categoryUrl: string
  };

  constructor() {
    this.urls = window.__globalConfig.apiUrls.product;
    pigeon.subscribe('wishlistDialog:itemIsOnWishlist', this.onWishlistDialogChange);
  }

  addToBasket(product: any, quantity: number, trackingList: string = '', greetingText: string = ''): Promise<{ data: AddToBasketResponse }> {
    let formData = jsGlobalConfig().getCsrfTokenData();
    const currentCategory = productListStore.currentCategory;
    formData['itemId'] = product.itemId;
    formData['productId'] = product.id;
    formData['quantity'] = quantity;
    formData['categoryId'] = currentCategory && currentCategory.id ? currentCategory.id : null;
    formData['wishlist'] = false;
    formData['title'] = document.title;
    formData['greetingText'] = greetingText;
    if ('trackingData' in product) {
      formData['trackingList'] = getTrackingListValue({
        ...product.trackingData,
        list: trackingList
      });
    }

    if (product.soldoutRelation && product.soldoutRelation.itemId) {
      formData['referredBy'] = product.trackingData.wban;
      formData['itemId'] = product.soldoutRelation.itemId;
    }

    return httpRequest(this.urls.addToBasket, {
      method: 'POST',
      data: encodeFormData(formData)
    }).then(response => {
      if (response.data.productDetails && response.data.productDetails.product) {
        const responseProductStore = response.data.productDetails.product;
        productListStore.addToBasket(responseProductStore);
        if (product.soldoutRelation && product.soldoutRelation.itemId) {
          productListStore.addSuccessorToBasket(product.itemId);
        }
        pigeon.publish('productaddedToCart:updateCounter', response.data.count);
        tracking.fireAddToCart(response.data.trackingData);
      }
      return response;
    });
  }

  addToWishlist(productId: string, itemId: string, trackList: string) {
    return productService.addToWishlist(productId, itemId, trackList)
      .then((response) => {
        productListStore.addToWishlist(productId, itemId);
        return response;
      });
  }

  removeFromWishlist(productId: string, itemId: string, trackingList: string) {
    return productService.removeFromWishlist(productId, itemId, trackingList)
      .then((response) => {
        if (!response.data.showDialog) {
          productListStore.removeFromWishlist(productId, itemId);
        }

        return response;
      })
  }

  changeQuantity(productId: string, itemId: string, quantity: number) {
    if (typeof quantity === 'undefined') {
      const err = {
        name: '',
        message: `Quantity undefined (productId=${productId},itemId=${itemId})`,
        stack: '',
      };
      window.onerror(err.message, 'ProductsListService::changeQuantity', null, null, err);
      quantity = 1;
    }

    let formData = jsGlobalConfig().getCsrfTokenData();
    formData['productId'] = productId;
    formData['itemId'] = itemId;
    formData['selectedQuantity'] = quantity;

    httpRequest(this.urls.changeQuantity, {
      method: 'POST',
      data: encodeFormData(formData)
    }).then((response) => {
        const responseProductStore = response.data;
        productListStore.updateProduct(responseProductStore);
      });
  }

  changeParameters(newParams: Parameters, options: ChangeParameterOptions = {}): Promise<any> {
    const parameters: Parameters = this.buildParameters(newParams);
    productListStore.filterCurrentParams = parameters;
    return this.updateProducts(parameters, options);
  }

  removeParameterOption(parameterName: string, option: string, options: ChangeParameterOptions = {}): Promise<any> {
    const parameters: Parameters = this.buildParameters({});
    const index = parameters[parameterName].indexOf(option);
    if (index > -1) {
      parameters[parameterName].splice(index, 1);
    }
    return this.updateProducts(parameters, options);
  }

  removeAllFilters(options: ChangeParameterOptions = {}): Promise<any> {
    const activeFilters = productListStore.getActiveFilters();
    const params = {};
    Object.keys(activeFilters).forEach(key => {
      params[key] = [];
    });

    return this.changeParameters(params, options);
  }

  buildParameters(newParams: Parameters): Parameters {
    // Get stored filter parameters
    const activeFilters = productListStore.getActiveFilters();
    const filters: Parameters = {};
    Object.keys(activeFilters).forEach(key => {
      filters[key] = activeFilters[key].map(f => f.value)
    });

    // Get stored page parameter
    const page: Parameters = {};
    page[ProductsListService.pageParameterName] = [
      (productListStore.pagingData.currentPage - 1).toString()
    ];

    // Get stored sorting parameter
    const activeSort = productListStore.getActiveSort();
    const sort: Parameters = {};
    if (activeSort) {
      sort[ProductsListService.sortParameterName] = [
        activeSort.value
      ];
    }

    // Get search query
    const queryTerm = productListStore.getSearchQuery();
    const search: Parameters = {};
    if (queryTerm) {
      search[ProductsListService.searchParameterName] = [queryTerm];
    }

    const showAll: Parameters = {};
    if (location.search.split(/[?&]/).filter(param => param === 'show=all').length > 0) {
      showAll['show'] = ['all'];
    }
    const previousCategoryId: Parameters = {'previousCategoryId': [productListStore.categoryForContent != null ? productListStore.categoryForContent.id : null]};

    const fromActiveVirtualFilterCategory: Parameters = {'fromActiveVirtualFilterCategory': [String(productListStore.activeVirtualFilterCategory)]};

    const fromActivePhysicalFilterCategory: Parameters = {'fromActivePhysicalFilterCategory': [String(productListStore.activePhysicalFilterCategory)]};

    // Merge all parameters and apply new parameters
    const allParams: Parameters = {
      ...search, ...filters, ...sort, ...page, ...showAll,
      ...previousCategoryId, ...fromActiveVirtualFilterCategory, ...fromActivePhysicalFilterCategory
    };
    Object.keys(newParams).forEach(key => {
      allParams[key] = newParams[key];
    });

    // Remove page/sorting parameters that represent the default value
    if (
      productListStore.sortingData.length &&
      productListStore.sortingData[0].value === allParams[ProductsListService.sortParameterName][0]
    ) {
      delete allParams[ProductsListService.sortParameterName];
    }

    if (parseInt(allParams[ProductsListService.pageParameterName][0]) === 0) {
      delete allParams[ProductsListService.pageParameterName];
    }

    // Remove empty parameters
    Object.keys(allParams).forEach((key) => {
      if (allParams[key].length === 0) {
        delete allParams[key];
      }
    });

    return allParams;
  }

  protected updateProducts(params: Parameters, options: ChangeParameterOptions = {}) {
    const defaultOptions: ChangeParameterOptions = {pageLoadAllowed: false, elementPlacement: 'replace'};
    const opts = {...defaultOptions, ...options};

    const url = this.urls.categoryUrl;
    const moreParams = ['ajax=true'];
    if (opts.elementPlacement !== 'replace' && 'p' in params) {
      moreParams.push('withInsertions=false');
    }
    const parameterStr = this.createParametersString(params);
    const requestUrl = `${url}?${parameterStr}&${moreParams.join('&')}`;

    pigeon.publish('productList:loadStart', parameterStr);

    return httpRequest(requestUrl)
      .then(response => {
        productListStore.filterPageLoad.url = response.data.url;
        if (response.data.reloadPage === true) {
          if (opts.pageLoadAllowed) {
            window.location.href = response.data.url;
            return null;
          } else {
            productListStore.filterPageLoad.reload = true;
          }
        }

        pigeon.publish('productList:loadComplete', response.data);
        productListStore.setData(response.data, options.elementPlacement);
      });
  }

  reloadIfNeeded() {
    if (productListStore.filterPageLoad.reload) {
      window.location.href = productListStore.filterPageLoad.url;
    }
  }

  saveOriginalFilterState() {
    const params = this.buildParameters({});
    productListStore.filterOriginalParams = params;
    productListStore.filterCurrentParams = params;
  }

  restoreOriginalFilterState() {
    productListStore.filterCurrentParams = {...productListStore.filterOriginalParams};
    productListStore.filterPageLoad = {url: null, reload: false};
    return this.updateProducts(productListStore.filterOriginalParams);
  }

  createParametersString(parameters: Parameters): string {
    const parts: string[] = [];
    Object.keys(parameters).forEach((key) => {
      const paramValues: string[] = parameters[key];
      paramValues.forEach(value => {
        parts.push(`${key}=${encodeURIComponent(value)}`);
      })
    });

    return parts.join('&');
  }

  onWishlistDialogChange({isItemOnWishlist, productId, itemId}) {
    if (isItemOnWishlist) {
      productListStore.addToWishlist(productId, itemId);
    } else {
      productListStore.removeFromWishlist(productId, itemId);
    }
  }

  getInsertion(categoryId: string, teaserId: string): Promise<any> {
    const teaserIdBase64 = window.btoa(teaserId);
    return httpRequest(`/teasers/category/${categoryId}/${teaserIdBase64}/`)
      .then(response => {
        return response.data;
      }).catch(() => {
        return null;
      });
  }
}


const productsListService = new ProductsListService();
export default productsListService;
export {ProductsListService}
